import { useEffect, useEffectEvent, useState } from 'react'

/** Upper bound on how long a parked click waits: a refresh takes ~1s, a block poll ~10s. */
export const DEFERRED_SUBMIT_MAX_WAIT_MS = 15_000

/**
 * Lets a review CTA stay enabled while a background refresh (block poll, requirements re-key,
 * re-simulation) is in flight: a click that lands mid-refresh is parked and replayed once
 * `isRefreshing` settles, so `submit` always runs against fresh inputs and the button never
 * flashes disabled for the ~1s the refresh takes.
 *
 * `canSubmit` is the button's own enabled condition (minus the refresh signal), re-read from the
 * latest render when the parked click is replayed: the refresh may have flipped a gate the
 * `disabled` prop would otherwise have enforced (blacklist, approval error, risk ack, wrong
 * chain…), so a replay that no longer passes it is dropped instead of bypassing the button.
 * `submit` is likewise read from the latest render, so it sees the post-refresh data.
 *
 * A parked click is a bridge, not a queue: it is dropped as soon as `shouldCancel` turns true
 * (a hard block surfaced mid-refresh, e.g. a rebuild error that keeps the refresh signal pinned),
 * and after `maxWaitMs` regardless, so a refresh that never settles can neither replay a click
 * from long ago nor leave the CTA on "Loading" forever.
 */
export function useDeferredSubmit<T>({
  isRefreshing,
  canSubmit,
  shouldCancel = false,
  maxWaitMs = DEFERRED_SUBMIT_MAX_WAIT_MS,
  submit
}: {
  isRefreshing: boolean
  canSubmit: boolean
  shouldCancel?: boolean
  maxWaitMs?: number
  submit: (arg: T) => void
}) {
  const [pending, setPending] = useState<{ arg: T } | null>(null)

  const settle = useEffectEvent(() => {
    if (pending === null) return
    if (shouldCancel) {
      setPending(null)
      return
    }
    if (isRefreshing) return
    setPending(null)
    if (canSubmit) submit(pending.arg)
  })

  useEffect(() => {
    settle()
  }, [pending, isRefreshing, shouldCancel])

  useEffect(() => {
    if (pending === null) return
    const timeout = setTimeout(() => setPending(null), maxWaitMs)
    return () => clearTimeout(timeout)
  }, [pending, maxWaitMs])

  const request = (arg: T) => {
    if (shouldCancel) return
    if (isRefreshing) {
      setPending({ arg })
      return
    }
    if (canSubmit) submit(arg)
  }

  return { request, isPending: pending !== null }
}

/**
 * Confirm-button gate for the review dialogs.
 *
 * - `isHardBlocked`: wallet / chain / build error / blacklist / risk gates — always disable, and
 *   cancel a parked click the moment they surface (even mid-refresh).
 * - `isPrepared`: the current transaction + requirements + simulation are ready to sign.
 * - `isRefreshSignal`: a compatible refresh is bridging previous data (placeholder pair,
 *   re-simulation). Only counts once a bundle has been shown at least once, so the first load
 *   still disables the CTA.
 *
 * While refreshing, `isDisabled` stays false and a click is parked via `useDeferredSubmit`; it is
 * replayed only if `!isHardBlocked && isPrepared` still holds once the refresh settles (the same
 * gates `isDisabled` enforces), and `submit` re-checks its own guards on top.
 */
export function useReviewConfirmGate<T>({
  isHardBlocked,
  isPrepared,
  isRefreshSignal,
  submit
}: {
  isHardBlocked: boolean
  isPrepared: boolean
  isRefreshSignal: boolean
  submit: (arg: T) => void
}) {
  const [hasPrepared, setHasPrepared] = useState(false)
  if (!hasPrepared && isPrepared && !isHardBlocked) setHasPrepared(true)
  const isRefreshing = hasPrepared && isRefreshSignal

  const { request, isPending } = useDeferredSubmit<T>({
    isRefreshing,
    canSubmit: !isHardBlocked && isPrepared,
    shouldCancel: isHardBlocked,
    submit
  })
  const isDisabled = isHardBlocked || (!isPrepared && !isRefreshing) || isPending

  return { request, isPending, isRefreshing, isDisabled }
}
